export interface GanttSharedThemeDimensions {
    rowHeight: number;
    barHeight: number;
}

type TaskStatusCssVar = '--gantt-task-status' | '--portfolio-gantt-task-status';

function timelineBlock(roots: string, dims: GanttSharedThemeDimensions, statusVar: TaskStatusCssVar): string {
    const { rowHeight, barHeight } = dims;
    return `
    ${roots} .gantt_task_cell.gantt_current_week:not(.weekend),
    ${roots} .gantt_scale_cell.gantt_current_week:not(.weekend) {
      background: var(--gantt-current-week-bg) !important;
    }

    ${roots} .gantt_task_cell.gantt_current_week.weekend,
    ${roots} .gantt_scale_cell.gantt_current_week.weekend {
      background: color-mix(in oklch, var(--muted) 26%, transparent) !important;
    }

    ${roots} .gantt_task_scale .gantt_scale_line:last-child .gantt_scale_cell.gantt_today {
      background: transparent !important;
      color: var(--foreground) !important;
      font-weight: 600 !important;
      box-shadow: inset 0 0 0 2px #dc2626 !important;
      border-radius: 9999px !important;
    }

    .dark ${roots} .gantt_task_scale .gantt_scale_line:last-child .gantt_scale_cell.gantt_today {
      box-shadow: inset 0 0 0 2px #f87171 !important;
    }

    ${roots} .gantt_scale_line:first-child .gantt_scale_cell.gantt_today {
      background: color-mix(in oklch, var(--muted) 35%, transparent) !important;
      color: var(--foreground) !important;
      font-weight: 600 !important;
      box-shadow: none !important;
      border-radius: 0 !important;
    }

    ${roots} .gantt_task_cell.gantt_today:not(.weekend) {
      background: transparent !important;
    }

    ${roots} .gantt_task_cell.gantt_today.weekend {
      background: var(--gantt-weekend-bg) !important;
    }

    ${roots} .gantt_task_scale .gantt_scale_cell {
      border-right: none !important;
      border-bottom: 1px solid color-mix(in oklch, var(--border) 35%, transparent) !important;
    }

    ${roots} .gantt_task_bg .gantt_task_cell {
      border-right: none !important;
      border-bottom: none !important;
    }

    ${roots} .gantt_ver_line {
      border-right: none !important;
      opacity: 0 !important;
    }

    ${roots} .gantt_task_row {
      border-bottom: 1px solid color-mix(in oklch, var(--border) 35%, transparent) !important;
    }

    ${roots} .gantt_grid_data .gantt_row.gantt_has_child {
      background: color-mix(in oklch, var(--muted) 12%, transparent) !important;
    }

    ${roots} .gantt_task_row.gantt_has_child {
      background: color-mix(in oklch, var(--muted) 8%, transparent) !important;
    }

    ${roots} .gantt_grid_data .gantt_row.gantt_has_child .gantt_cell .task-name {
      font-weight: 600 !important;
    }

    ${roots} .gantt_task_line {
      border-radius: 8px !important;
      border: 1px solid hsl(var(${statusVar}) / 0.28) !important;
      background: color-mix(in oklch, var(--card) 98%, transparent) !important;
      box-shadow:
        0 0 0 1px hsl(var(${statusVar}) / 0.12),
        0 0 20px -4px hsl(var(${statusVar}) / 0.35) !important;
    }

    ${roots} .gantt_task_line:hover {
      border-color: hsl(var(${statusVar}) / 0.45) !important;
      box-shadow:
        0 0 0 1px hsl(var(${statusVar}) / 0.18),
        0 0 24px -2px hsl(var(${statusVar}) / 0.42) !important;
    }

    ${roots} .gantt_task_line::before {
      left: 8px;
      width: 7px;
      height: 7px;
    }

    ${roots} .gantt_task_content {
      padding: 0 8px 0 22px !important;
      text-align: left !important;
      justify-content: flex-start !important;
      font-size: 12px !important;
      height: ${barHeight}px !important;
      line-height: ${barHeight}px !important;
    }

    ${roots} .gantt_task_line {
      height: ${barHeight}px !important;
      margin-top: ${Math.max(0, (rowHeight - barHeight) / 2)}px !important;
    }
  `;
}

export function buildProjectGanttTimelineCss(dims: GanttSharedThemeDimensions): string {
    const roots = '.gantt-container .gantt_container, .gantt-container-fullscreen .gantt_container';
    const darkRoots = '.dark .gantt-container .gantt_container, .dark .gantt-container-fullscreen .gantt_container';
    return `
    ${roots} {
      --gantt-current-week-bg: color-mix(in oklch, var(--muted) 32%, transparent);
    }
    ${darkRoots} {
      --gantt-current-week-bg: color-mix(in oklch, var(--muted) 24%, transparent);
    }
    ${timelineBlock(roots, dims, '--gantt-task-status')}
  `;
}

export function buildPortfolioGanttTimelineCss(dims: GanttSharedThemeDimensions): string {
    const roots = '.portfolio-gantt-card-chart .gantt_container';
    const darkRoots = '.dark .portfolio-gantt-card-chart .gantt_container';
    return `
    ${roots} {
      --gantt-current-week-bg: color-mix(in oklch, var(--muted) 32%, transparent);
      --gantt-weekend-bg: var(--portfolio-gantt-weekend-bg);
    }
    ${darkRoots} {
      --gantt-current-week-bg: color-mix(in oklch, var(--muted) 24%, transparent);
      --gantt-weekend-bg: var(--portfolio-gantt-weekend-bg);
    }
    ${timelineBlock(roots, dims, '--portfolio-gantt-task-status')}
  `;
}
